/**
 * Utilidades para manipulação e validação de tokens JWT
 */

/**
 * Decodifica o payload de um token JWT sem validar assinatura
 * @param token - Token JWT
 * @returns Payload decodificado ou null se inválido
 */
export function decodeJWT(token: string): Record<string, unknown> | null {
  try {
    if (!token || typeof token !== 'string') {
      return null;
    }
    
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }
    
    const payload = parts[1];
    if (!payload) {
      return null;
    }
    
    const decoded = atob(payload);
    if (!decoded) {
      return null;
    }
    
    return JSON.parse(decoded);
  } catch {
    return null;
  }
}

/**
 * Verifica se um token JWT está expirado
 * @param token - Token JWT
 * @returns true se o token está expirado ou inválido, false caso contrário
 */
export function isTokenExpired(token: string | null): boolean {
  if (!token) {
    return true;
  }

  try {
    const payload = decodeJWT(token);
    if (!payload || !payload.exp) {
      return true;
    }

    const expirationTime = (payload.exp as number) * 1000; // Converter de segundos para milissegundos
    const currentTime = Date.now();
    
    return expirationTime < currentTime;
  } catch {
    return true;
  }
}

/**
 * Obtém o token válido do localStorage
 * @returns Token válido ou null se não houver ou estiver expirado
 */
export function getValidToken(): string | null {
  const token = localStorage.getItem('auth_token');
  
  if (!token || isTokenExpired(token)) {
    return null;
  }
  
  return token;
}

/**
 * Obtém informações do token JWT
 * @param token - Token JWT
 * @returns Informações do token incluindo tempo restante até expiração
 */
export function getTokenInfo(token: string): {
  userId?: string;
  email?: string;
  role?: string;
  expiresAt?: Date;
  isExpired: boolean;
  timeRemaining?: number; // em segundos
} | null {
  const payload = decodeJWT(token);
  
  if (!payload) {
    return null;
  }

  const isExpired = isTokenExpired(token);
  const expiresAt = payload.exp ? new Date((payload.exp as number) * 1000) : undefined;
  const timeRemaining = payload.exp && !isExpired 
    ? Math.floor(((payload.exp as number) * 1000 - Date.now()) / 1000)
    : 0;

  return {
    userId: payload.sub as string | undefined,
    email: payload.email as string | undefined,
    role: payload.role as string | undefined,
    expiresAt,
    isExpired,
    timeRemaining,
  };
}
